import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowRight, Check } from 'lucide-react'

const STORAGE_KEY = 'poly:email-capture:dismissed'
const SCROLL_TRIGGER_RATIO = 0.5 // show after user scrolls past 50% of page
const TIME_TRIGGER_MS = 30000   // or after 30 seconds of session

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function EmailCapturePopup() {
    const [open, setOpen] = useState(false)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [errorMsg, setErrorMsg] = useState('')

    useEffect(() => {
        if (typeof window === 'undefined') return
        if (window.localStorage.getItem(STORAGE_KEY)) return

        let triggered = false
        const trigger = () => {
            if (triggered) return
            triggered = true
            cleanup()
            setOpen(true)
        }

        const onScroll = () => {
            const scrollY = window.scrollY
            const docHeight = document.documentElement.scrollHeight - window.innerHeight
            if (docHeight > 0 && scrollY / docHeight >= SCROLL_TRIGGER_RATIO) trigger()
        }

        const timer = window.setTimeout(trigger, TIME_TRIGGER_MS)
        window.addEventListener('scroll', onScroll, { passive: true })

        const cleanup = () => {
            window.clearTimeout(timer)
            window.removeEventListener('scroll', onScroll)
        }

        return cleanup
    }, [])

    const dismiss = (persist = true) => {
        if (persist && typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, '1')
        }
        setOpen(false)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (status === 'submitting') return
        const trimmedFirst = firstName.trim()
        const trimmedLast = lastName.trim()
        if (!trimmedFirst || !trimmedLast) {
            setStatus('error')
            setErrorMsg('Enter your full name')
            return
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setStatus('error')
            setErrorMsg('Enter a valid email')
            return
        }

        setStatus('submitting')
        setErrorMsg('')

        try {
            const res = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ firstName: trimmedFirst, lastName: trimmedLast, email }),
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Submission failed')

            setStatus('success')
            window.PolyAnalytics?.trackSignup(email, 'email_capture_popup')
            window.setTimeout(() => dismiss(true), 3500)
        } catch (err) {
            console.error('Email capture submit failed', err)
            setStatus('error')
            setErrorMsg('Something went wrong. Try again.')
        }
    }

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: 24, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 24, scale: 0.96 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby="email-capture-title"
                    className="!fixed bottom-6 right-6 z-[60] w-[min(380px,calc(100vw-3rem))] bg-popover rounded-2xl border border-border/80 shadow-2xl p-5"
                >
                    <button
                        type="button"
                        onClick={() => dismiss(true)}
                        aria-label="Dismiss"
                        className="absolute top-3 right-3 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    {status === 'success' ? (
                        <div className="flex flex-col items-start gap-3 pr-6">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary">
                                <Check className="w-5 h-5" />
                            </span>
                            <h3
                                id="email-capture-title"
                                className="font-heading text-lg text-foreground"
                            >
                                You're on the list.
                            </h3>
                            <p className="font-body text-sm text-muted-foreground font-light">
                                We'll reach out with your early access details.
                            </p>
                        </div>
                    ) : (
                        <>
                            <p className="font-body text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                                Early access
                            </p>
                            <h3
                                id="email-capture-title"
                                className="font-heading text-lg text-foreground leading-tight mb-1.5 pr-6"
                            >
                                Get Early Access to Poly
                            </h3>
                            <p className="font-body text-sm text-muted-foreground font-light mb-4">
                                Be among the first to deploy your own digital workforce — spots are limited.
                            </p>

                            <form onSubmit={handleSubmit} className="flex flex-col gap-2.5">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        required
                                        value={firstName}
                                        onChange={(e) => {
                                            setFirstName(e.target.value)
                                            if (status === 'error') setStatus('idle')
                                        }}
                                        placeholder="First name"
                                        aria-label="First name"
                                        className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/60 transition-colors"
                                        disabled={status === 'submitting'}
                                    />
                                    <input
                                        type="text"
                                        required
                                        value={lastName}
                                        onChange={(e) => {
                                            setLastName(e.target.value)
                                            if (status === 'error') setStatus('idle')
                                        }}
                                        placeholder="Last name"
                                        aria-label="Last name"
                                        className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/60 transition-colors"
                                        disabled={status === 'submitting'}
                                    />
                                </div>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value)
                                        if (status === 'error') setStatus('idle')
                                    }}
                                    placeholder="you@company.com"
                                    aria-label="Your work email"
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/60 transition-colors"
                                    disabled={status === 'submitting'}
                                />
                                <button
                                    type="submit"
                                    disabled={status === 'submitting'}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-body text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    {status === 'submitting' ? 'Saving…' : (
                                        <>
                                            Request early access
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                                {status === 'error' && errorMsg && (
                                    <p className="font-body text-xs text-red-500" role="alert">
                                        {errorMsg}
                                    </p>
                                )}
                                <p className="font-body text-[11px] text-muted-foreground/80 font-light">
                                    One email. No spam. Unsubscribe anytime.
                                </p>
                            </form>
                        </>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}
